import { useState } from "react";
import { homeService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import {
  IApiRequest,
  ICategoriesResponse,
  ICategoryDropsResponse,
} from "@miplatform/types";
import { createUUID, simpleSort } from "@miplatform/utils";

const GET_CATEGORIES_UUID = createUUID();
const GET_RECENT_CATEGORIES_UUID = createUUID();

export const useCategories = () => {
  const [categories, setCategories] = useState<ICategoriesResponse[]>([]);
  const [recentCategories, setRecentCategories] = useState<
    ICategoriesResponse[]
  >([]);
  const [categoryFilterOptions, setCategoryFilterOptions] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [categoryDrops, setCategoryDrops] = useState<ICategoryDropsResponse[]>(
    []
  );

  const stateManager = useComponentState();

  const getCategories = async ({
    top,
    skip,
    search = "",
  }: IApiRequest = {}) => {
    stateManager.abort(GET_CATEGORIES_UUID);

    const response = await stateManager.run(async () => {
      return homeService.categories.getAll({
        sort: "name",
        top,
        skip,
        filters: { name: { contains: search } },
      });
    }, GET_CATEGORIES_UUID);
    if (response.status === HTTP_STATUSES.OK) {
      setCategories(response.data);
    }
    return response;
  };

  const getRecentCategories = async ({
    sort,
    filters,
    top,
    search = "",
  }: IApiRequest = {}) => {
    stateManager.abort(GET_RECENT_CATEGORIES_UUID);

    const response = await stateManager.run(async () => {
      return homeService.categories.getAll({
        sort,
        top,
        filters: {
          ...filters,
          name: { contains: search },
        },
      });
    }, GET_RECENT_CATEGORIES_UUID);
    if (response.status === HTTP_STATUSES.OK) {
      setRecentCategories(response.data);
    }
    return response;
  };

  const handleSetCategories = (data: ICategoriesResponse[]) => {
    const categoriesWithDisplayOrder = data.filter((c) => !!c.displayOrder);
    const categoriesWithoutDisplayOrder = data.filter((c) => !c.displayOrder);

    const sortedWithDisplayOrder = categoriesWithDisplayOrder.sort((a, b) => {
      if (a.displayOrder === b.displayOrder) {
        return simpleSort(
          a.name?.toLowerCase() || "",
          b.name?.toLowerCase() || ""
        );
      }
      return simpleSort(a.displayOrder || 0, b.displayOrder || 0);
    });

    const sortedWithoutDisplayOrder = categoriesWithoutDisplayOrder.sort(
      (a, b) =>
        simpleSort(a.name?.toLowerCase() || "", b.name?.toLowerCase() || "")
    );

    setCategories([...sortedWithDisplayOrder, ...sortedWithoutDisplayOrder]);
  };

  const getCategoryDetails = (id: string) => {
    return stateManager.run(() => {
      return homeService.categories.getById({ id });
    });
  };

  const createCategory = (data: ICategoriesResponse) => {
    return stateManager.run(() => {
      return homeService.categories.create({
        data,
        toastMessageConfig: {
          success: {
            text: `Successfully created Category: ${data.name}.`,
          },
        },
      });
    });
  };

  const updateCategory = (data: Partial<ICategoriesResponse>) => {
    return stateManager.run(() => {
      return homeService.categories.update({
        id: data.id,
        data,
        toastMessageConfig: {
          success: {
            text: `Successfully updated Category: ${data.name}.`,
          },
        },
      });
    });
  };

  const deleteCategory = (data: Partial<ICategoriesResponse>) => {
    stateManager.run(async () => {
      const response = await homeService.categories.delete({
        id: data.id,
        modifiedOn: data.modifiedOn,
        toastMessageConfig: {
          success: {
            text: `${data.name} was deleted successfully.`,
          },
          failure: {
            text: `Failed to delete ${data.name}.`,
          },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        setRefresh((prev) => !prev);
      }
    });
  };

  const getCategoryFilters = () => {
    return stateManager.run(() => {
      return homeService.categories.getFilters();
    });
  };

  const getCategoryDrops = async () => {
    return stateManager.run(async () => {
      const response = await homeService.categories.drops.getAll({
        sort: "name",
      });
      if (response.status === HTTP_STATUSES.OK) {
        setCategoryDrops(response.data);
      }
      return response;
    });
  };

  const createCategoryDrop = (
    data: { name: string; dropTypeId: string },
    categoryId: string
  ) => {
    return stateManager.run(async () => {
      return homeService.categories.drops.create({
        categoryId,
        data,
      });
    });
  };

  const deleteCategoryDrop = (
    categoryId: string,
    data: Partial<ICategoryDropsResponse>
  ) => {
    return stateManager.run(async () => {
      return homeService.categories.drops.delete({
        categoryId,
        dropId: data.id,
        data,
      });
    });
  };

  return {
    isLoading: stateManager.isBusy(),
    categories,
    recentCategories,
    categoryFilterOptions,
    refresh,
    getCategories,
    getRecentCategories,
    handleSetCategories,
    getCategoryDetails,
    getCategoryFilters,
    setCategories,
    setRecentCategories,
    setCategoryFilterOptions,
    deleteCategory,
    updateCategory,
    createCategory,
    getCategoryDrops,
    categoryDrops,
    setCategoryDrops,
    createCategoryDrop,
    deleteCategoryDrop,
  };
};
